import { Flex, Link } from '@iheartjane/reefer';
import type { CategoryTabStyle, MenuTab } from '@iheartjane/types';
import { clsx } from 'clsx';
import { useEffect, useRef } from 'react';
import { hex } from 'wcag-contrast';

import { CategoryTab } from './categoryTab/categoryTab';
import styles from './categoryTabs.module.css';

export interface CategoryTabsProps {
  categories: MenuTab[];
  customLabels?: Record<string, string>;
  getUrlString: (category: MenuTab) => string;
  onClick?: (category: MenuTab) => void;
  // Used for skeleton previews in Biz Admin
  preview?: boolean;
  primaryColor: string;
  selectedCategory: MenuTab;
  storeState?: string;
  variant: CategoryTabStyle;
}

export const CategoryTabs = ({
  categories,
  customLabels,
  onClick,
  getUrlString,
  preview,
  primaryColor,
  selectedCategory,
  storeState,
  variant = 'icon',
}: CategoryTabsProps) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const isDarkPrimaryColor = hex(primaryColor, '#ffffff') >= 3;

  useEffect(() => {
    const scrollContainer = containerRef.current;
    const selectedElement = scrollContainer?.querySelector<HTMLElement>(
      '[data-selected="true"]'
    );

    if (selectedElement && scrollContainer) {
      const { offsetLeft, offsetWidth: elementWidth } = selectedElement;
      const { offsetWidth: containerWidth } = scrollContainer;

      scrollContainer.scrollTo({
        behavior: 'smooth',
        left: offsetLeft - (containerWidth - elementWidth) / 2,
      });
    }
  }, [selectedCategory]);

  return (
    <Flex
      ref={containerRef}
      className={clsx(styles['scroll'], styles[`${variant}`])}
      overflowX="scroll"
    >
      {categories.map((category) => (
        <Link
          className={
            category !== selectedCategory ? styles['unselected'] : undefined
          }
          variant="minimal"
          to={getUrlString(category)}
          key={category}
        >
          <div data-selected={category === selectedCategory}>
            <CategoryTab
              preview={preview}
              darkPrimaryColor={isDarkPrimaryColor}
              name={category}
              customLabel={customLabels?.[category]}
              selected={category === selectedCategory}
              storeState={storeState}
              variant={variant}
              onClick={onClick}
            />
          </div>
        </Link>
      ))}
    </Flex>
  );
};
